// client/src/components/auth/Login.tsx (TypeScript since ST-00216)
// ST-00303: when LexiBridge cannot be reached, the Can't reach LexiBridge card shows
// in place of the form, and a sign-in that cannot get through says so.
import {
  forwardRef,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ComponentType,
  type CSSProperties,
  type FormEvent,
  type RefObject,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, ACCESS_NOTICES } from '../../context/AuthContext';
import { LogIn, ShieldOff, ArrowRight, Lock } from 'lucide-react';
import { animate, stagger, createTimeline } from 'animejs';
import './Login.css';
import PasswordInputJs from '../common/PasswordInput';
import ConnectionProblem from './ConnectionProblem';
import { CONNECTION_PROBLEM_MESSAGE } from './connectionProblemText';
import type { AuthParticle, PasswordInputProps } from '../../types/clientShell';

export type { AuthParticle };

// PasswordInput.jsx is still JavaScript; its JSDoc would otherwise type the
// whole props object as the `tone` union.
const PasswordInput = PasswordInputJs as unknown as ComponentType<PasswordInputProps>;

// Maze path data — geometric right-angle paths inspired by the LexiBridge logo divider
const MAZE_PATHS = [
  // Top-left cluster
  'M 0,80 L 140,80 L 140,220 L 280,220',
  'M 60,0 L 60,150 L 220,150',
  'M 0,280 L 80,280 L 80,160 L 180,160',
  // Top-right cluster
  'M 1440,60 L 1280,60 L 1280,200',
  'M 1350,0 L 1350,120 L 1180,120 L 1180,40',
  'M 1440,220 L 1360,220 L 1360,140',
  // Bottom-left cluster
  'M 0,640 L 160,640 L 160,760 L 300,760',
  'M 80,900 L 80,720 L 220,720',
  'M 0,800 L 60,800 L 60,700',
  // Bottom-right cluster
  'M 1440,680 L 1300,680 L 1300,800',
  'M 1380,900 L 1380,740 L 1220,740',
  'M 1440,840 L 1380,840 L 1380,760',
  // Sparse center hints
  'M 480,380 L 560,380 L 560,320',
  'M 880,520 L 880,580 L 960,580',
];

// Generate random particles with varied shapes and colors
const generateParticles = (count: number): AuthParticle[] => {
  const shapes: AuthParticle['shape'][] = ['circle', 'square', 'diamond'];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 2 + Math.random() * 3,
    shape: shapes[Math.floor(Math.random() * shapes.length)]!,
    color:
      Math.random() > 0.75 ? '#3a9e93' : Math.random() > 0.5 ? 'rgba(255,255,255,0.7)' : '#45B7AA',
  }));
};

export interface AuthBackgroundProps {
  particles?: AuthParticle[] | null;
  mazeRef?: RefObject<SVGSVGElement | null>;
  particlesRef?: RefObject<HTMLDivElement | null>;
}

// Shared background renderer — exported for use in Unauthorized.js
export const AuthBackground = forwardRef<unknown, AuthBackgroundProps>(
  ({ particles, mazeRef, particlesRef }, _ref) => (
    <>
      <div className="login-bg-grid" />
      <div className="login-bg-glow login-bg-glow--teal" />
      <div className="login-bg-glow login-bg-glow--purple" />
      <div className="login-bg-glow login-bg-glow--orange" />

      <svg
        className="login-maze"
        ref={mazeRef}
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
      >
        {MAZE_PATHS.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>

      {particles && (
        <div className="login-particles" ref={particlesRef}>
          {particles.map((p) => (
            <div
              key={p.id}
              className={`login-particle login-particle--${p.shape}`}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                backgroundColor: p.color,
              }}
            />
          ))}
        </div>
      )}
    </>
  )
);

AuthBackground.displayName = 'AuthBackground';

// Shared animation runner for background elements
export const runBackgroundAnimations = (
  mazeRef: RefObject<SVGSVGElement | null>,
  particlesRef: RefObject<HTMLDivElement | null>
): void => {
  try {
    // Maze line drawing
    const paths = mazeRef.current?.querySelectorAll('path');
    if (paths && paths.length > 0) {
      const pathArray = Array.from(paths);
      pathArray.forEach((path) => {
        const length = path.getTotalLength();
        // String(): what the style setter did with the number before the conversion.
        path.style.strokeDasharray = String(length);
        path.style.strokeDashoffset = String(length);
      });

      animate(pathArray, {
        strokeDashoffset: 0,
        opacity: { from: 0, to: 0.22 },
        duration: 2800,
        delay: stagger(220, { from: 'center' }),
        ease: 'inOutQuad',
        onComplete: () => {
          animate(pathArray, {
            opacity: [0.12, 0.28],
            duration: 4000,
            ease: 'inOutSine',
            loop: true,
            alternate: true,
            delay: stagger(400),
          });
        },
      });
    }

    // Particle entrance + float
    const particleEls = particlesRef.current?.querySelectorAll('.login-particle');
    if (particleEls && particleEls.length > 0) {
      const particleArray = Array.from(particleEls);
      animate(particleArray, {
        opacity: { from: 0, to: 0.35 },
        scale: { from: 0.3, to: 1 },
        duration: 2000,
        delay: stagger(60, { from: 'center' }),
        ease: 'outSine',
        onComplete: () => {
          particleArray.forEach((el) => {
            animate(el, {
              translateX: `${-12 + Math.random() * 24}px`,
              translateY: `${-12 + Math.random() * 24}px`,
              opacity: 0.1 + Math.random() * 0.4,
              duration: 4000 + Math.random() * 5000,
              ease: 'inOutSine',
              loop: true,
              alternate: true,
            });
          });
        },
      });
    }
  } catch (err) {
    console.warn('Background animation error:', err);
  }
};

// Shared card entrance animation
export const runCardEntrance = (cardRef: RefObject<HTMLDivElement | null>): void => {
  try {
    if (!cardRef.current) return;

    const children = cardRef.current.querySelectorAll('.login-animate');
    const tl = createTimeline();

    tl.add(
      cardRef.current,
      {
        opacity: { from: 0, to: 1 },
        translateY: { from: 40, to: 0 },
        scale: { from: 0.96, to: 1 },
        duration: 1000,
        ease: 'outExpo',
      },
      300
    );

    if (children.length > 0) {
      tl.add(
        Array.from(children),
        {
          opacity: { from: 0, to: 1 },
          translateY: { from: 16, to: 0 },
          duration: 700,
          delay: stagger(65),
          ease: 'outExpo',
        },
        '-=600'
      );
    }
  } catch (err) {
    // Graceful fallback
    console.warn('Card animation error, showing fallback:', err);
    if (cardRef.current) {
      cardRef.current.style.opacity = '1';
      cardRef.current.style.transform = 'none';
      const children = cardRef.current.querySelectorAll<HTMLElement>('.login-animate');
      children.forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }
  }
};

// Export shared data for reuse
export { MAZE_PATHS, generateParticles };

const loginInputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  fontSize: '0.95rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const Login = () => {
  const { authenticated, login, authEnabled, loading, authNotice, connectionProblem } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Why the app sent the user here (organisation suspended / expired), from the
  // session check or a /login?reason=... redirect (ST-00215, audit IAM-11).
  const reason = new URLSearchParams(location.search).get('reason');
  const notice = authNotice || (reason && ACCESS_NOTICES[reason]) || null;
  const cardRef = useRef<HTMLDivElement>(null);
  const mazeRef = useRef<SVGSVGElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);
  const animationsRan = useRef(false);
  const particles = useMemo(() => generateParticles(28), []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (authenticated) {
      navigate('/');
    }
  }, [authenticated, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!authEnabled) {
      navigate('/');
      return;
    }
    if (submitting) return;
    setFormError(null);
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res && res.success) {
      navigate(res.user && res.user.mustChangePassword ? '/set-password?first=1' : '/');
    } else if (res && res.unreachable) {
      // The password was never checked: say what is wrong, not "Login failed".
      setFormError(CONNECTION_PROBLEM_MESSAGE);
    } else {
      setFormError((res && res.error) || 'Login failed');
    }
  };

  const runAnimations = useCallback(() => {
    if (animationsRan.current) return;
    animationsRan.current = true;

    runBackgroundAnimations(mazeRef, particlesRef);
    runCardEntrance(cardRef);
  }, []);

  // Not while the Can't reach LexiBridge card shows: the entrance must run when
  // the form itself mounts, or the form stays at opacity 0.
  useEffect(() => {
    if (!loading && !connectionProblem) {
      const timer = setTimeout(runAnimations, 80);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, connectionProblem, runAnimations]);

  // === Loading State ===
  if (loading) {
    return (
      <div className="login-page">
        <div className="login-bg-grid" />
        <div className="login-bg-glow login-bg-glow--teal" />
        <div className="login-bg-glow login-bg-glow--purple" />
        <div className="login-card" style={{ opacity: 1, transform: 'none' }}>
          <div className="login-loader">
            <div className="login-loader-block" />
            <div className="login-loader-block" />
            <div className="login-loader-block" />
          </div>
          <p className="login-loading-text">Initializing secure connection...</p>
        </div>
      </div>
    );
  }

  // === Auth Disabled State ===
  if (!authEnabled) {
    return (
      <div className="login-page">
        <AuthBackground particles={particles} mazeRef={mazeRef} particlesRef={particlesRef} />
        <div className="login-card" ref={cardRef}>
          <div className="login-animate login-disabled-icon">
            <ShieldOff size={24} />
          </div>
          <h2 className="login-animate login-disabled-title">Development Mode</h2>
          <p className="login-animate login-disabled-text">
            Authentication is currently disabled. You&apos;ll be redirected to the dashboard.
          </p>
          <button
            onClick={() => navigate('/')}
            className="login-animate login-button login-button--secondary"
          >
            <ArrowRight size={18} />
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // === Can't reach LexiBridge (ST-00303) ===
  // The session check got no answer: the user may well be signed in already, so
  // do not ask for a password that could not be checked anyway.
  if (connectionProblem) {
    return <ConnectionProblem />;
  }

  // === Main Login State ===
  return (
    <div className="login-page">
      <AuthBackground particles={particles} mazeRef={mazeRef} particlesRef={particlesRef} />

      <div className="login-card" ref={cardRef}>
        <div className="login-animate login-logo">
          <span className="login-wordmark" role="img" aria-label="LexiBridge">
            <span className="lw-lexi">Lexi</span>
            <span className="lw-divider" />
            <span className="lw-bridge">Bridge</span>
          </span>
        </div>

        <div className="login-animate login-subtitle">Operations Dashboard</div>

        <div className="login-animate login-divider" />

        <h2 className="login-animate login-welcome">Welcome back</h2>
        <p className="login-animate login-prompt">Sign in with your credentials to continue</p>

        <form
          className="login-form"
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}
        >
          <input
            className="login-animate"
            type="email"
            autoComplete="username"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting}
            style={loginInputStyle}
          />
          <PasswordInput
            tone="dark"
            wrapperClassName="login-animate"
            autoComplete="current-password"
            placeholder="Password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={submitting}
            style={loginInputStyle}
          />
          {/* Not .login-animate: this is rendered after the one-shot entrance
              animation, so it must not start at opacity:0 or it stays invisible. */}
          {(formError || notice) && (
            <p
              role="alert"
              style={{
                margin: 0,
                padding: '10px 12px',
                borderRadius: '10px',
                background: 'rgba(248,113,113,0.12)',
                border: '1px solid rgba(248,113,113,0.4)',
                color: '#fca5a5',
                fontSize: '0.85rem',
              }}
            >
              {formError || notice}
            </p>
          )}
          <button type="submit" className="login-animate login-button" disabled={submitting}>
            <LogIn size={18} />
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p className="login-animate" style={{ marginTop: '14px', fontSize: '0.85rem' }}>
          <a href="/forgot-password" style={{ color: '#45B7AA', textDecoration: 'none' }}>
            Forgot your password?
          </a>
        </p>

        <div className="login-animate login-footer">
          <Lock className="login-footer-lock" size={12} />
          <p>End-to-end encrypted session</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
